import { Chip, Tooltip, alpha } from "@mui/material";
import { useAppTheme } from "@/hooks/useAppTheme";
import { ReimbursementRowForecast } from "@/types";

/**
 * Per-row state chip for a reimbursement.
 *
 * The per-claimant rollup chip (ReimbursementForecastChip) describes what
 * approval will do for a claimant's set of rows ("Creates 2 · 1 unlinked").
 * That overview can't tell the reviewer which row is which — this chip fills
 * that gap by labeling each individual row's disposition. Used in the flat
 * Reimbursements tab and in the nested table inside an expanded claimant
 * row, so the same vocabulary applies whether the reviewer is browsing per-
 * claimant or per-reimbursement.
 *
 * Color vocabulary mirrors the rollup chip:
 *   - "On file"           — outlined, disabled tone (no-op, row will be skipped)
 *   - "Needs link review" — warning tint (ambiguous SL-claim candidates)
 *   - "Unlinked"          — warning tint (no SL claim found, will load orphaned)
 *   - "Create"            — info tint (will load with confident SL-claim link)
 *
 * Records the loader will filter (EOR per-line rows) or collapse (duplicate
 * footer captures) are HIDDEN from the displayed record set in DocumentReviewTab,
 * so this chip never has to represent them — what's on screen is what approve
 * will create. AB#4455.
 */
interface ReimbursementRowStateChipProps {
  forecast: ReimbursementRowForecast | undefined;
}

const baseChipSx = {
  height: 20,
  "& .MuiChip-label": { px: 0.75, fontSize: "0.7rem", fontWeight: 600 },
};

export const ReimbursementRowStateChip = ({ forecast }: ReimbursementRowStateChipProps) => {
  const { theme } = useAppTheme();

  if (!forecast) return null;

  if (forecast.is_duplicate_on_file) {
    return (
      <Tooltip
        title="Already on file from a prior load. Approving will skip insertion for this row."
        placement="top"
        arrow
      >
        <Chip
          label="On file"
          size="small"
          variant="outlined"
          sx={{
            ...baseChipSx,
            borderColor: "divider",
            color: "text.disabled",
          }}
        />
      </Tooltip>
    );
  }

  if (forecast.link_confidence === "ambiguous") {
    return (
      <Tooltip
        title="Multiple stoploss claims could match this row. Approving creates the reimbursement; reviewer reconciles the link manually."
        placement="top"
        arrow
      >
        <Chip
          label="Needs link review"
          size="small"
          sx={{
            ...baseChipSx,
            bgcolor: alpha(theme.palette.warning.main, 0.15),
            color: "warning.dark",
          }}
        />
      </Tooltip>
    );
  }

  if (forecast.link_confidence === "none" || !forecast.linked_sl_claim_id) {
    return (
      <Tooltip
        title="No stoploss claim found for this row. Approving creates the reimbursement unlinked; reviewer attaches it manually."
        placement="top"
        arrow
      >
        <Chip
          label="Unlinked"
          size="small"
          sx={{
            ...baseChipSx,
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            color: "warning.dark",
          }}
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title="Approving will create this reimbursement, linked to the resolved stoploss claim."
      placement="top"
      arrow
    >
      <Chip
        label="Create"
        size="small"
        sx={{
          ...baseChipSx,
          bgcolor: alpha(theme.palette.info.main, 0.15),
          color: "info.dark",
        }}
      />
    </Tooltip>
  );
};
